import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { SplitButton, DropDownButton } from "@progress/kendo-react-buttons";

let prevEventOrigin;
const OperationButtons = ({ navigationRef, appNavigationDiv }) => {
  //states
  const [incomingOperations, setIncomingOperations] = useState([]); //holds the incoming operation configs

  // dropdown operations come in e.operationButtons.buttons
  // inline operations come in e.operationButtons
  const updateOperationButtons = (e) => {
    if (e?.operationButtons)
      if (JSON.stringify(e) !== JSON.stringify(incomingOperations)) {
        resetOperationButtons(); //?is this really needed? 🤔
        const operationButtons = e.operationButtons.buttons || e.operationButtons;
        operationButtons.map((eachButton) => {
          eachButton.buttonOrigin = e.eventOrigin;
        });
        // concatinate only if event origin is differnt else replace
        if (e.eventOrigin !== prevEventOrigin)
          setIncomingOperations((prev) => {
            return [...(Array.isArray(prev) ? prev : []), e];
          });
        else setIncomingOperations((prev) => e);
      }
    prevEventOrigin = e.eventOrigin;
  };

  //reserts the prevEventOrigin
  //resets the operation buttons to []
  //assigns the operation buttons to the prevpage in navigation
  const resetOperationButtons = () => {
    prevEventOrigin = undefined;
    setIncomingOperations((prev) => {
      if (!navigationRef.current) {
        //fallback
        console.warn("Navigation ref is not available");
        return [];
      }
      let pagesCopy = navigationRef?.current?.state?.pages;
      if (pagesCopy) {
        let prevPageIndex = pagesCopy.length > 1 ? pagesCopy.length - 2 : pagesCopy.length - 1;
        // let prevPageIndex = pagesCopy.length > 0 ? pagesCopy.length - 1 : 0;
        let prevPage = {
          ...pagesCopy[prevPageIndex],
          operationButtons: prev?.operationButtons || (Array.isArray(prev) && prev?.flatMap((each) => each.operationButtons)),
        };
        // let prevPage = { ...pagesCopy[prevPageIndex], operationButtons: prev?.flatMap((each) => each.operationButtons) };
        pagesCopy[prevPageIndex] = Object.keys(prevPage.operationButtons).length > 0 ? prevPage : pagesCopy[prevPageIndex];
        Object.keys(prevPage.operationButtons).length > 0 && navigationRef.current.setPages(pagesCopy);
      }
      return [];
    });
  };

  //resets the operation buttons to []
  //gets the current activepage from navigation and sets operation buttons using it
  const handleStepDownOfOperationButtons = () => {
    const currentPage = navigationRef.current.getActivePage();
    // generateOperationButtons(currentPage);
    currentPage && setIncomingOperations(currentPage);
  };

  //generated buttons from the config
  const generateOperationButton = (buttonObj) => {
    let { predefinedCallback, displayName, element, name, configurationCallback, className, title, icon, content } = buttonObj;

    if (displayName) {
      content = name;
    }

    const buttonWithTextStyle = {
      display: "flex",
      gap: "5px",
      alignItems: "center",
      borderRadius: "10px",
      backgroundColor: "white",
      color: "black",
    };
    const buttonStyles = {
      backgroundColor: "white",
      borderRadius: "10px",
      color: "black",
    };
    if (element) return element;
    else
      return (
        <Button
          style={content ? buttonWithTextStyle : buttonStyles}
          key={title}
          className={className}
          onClick={predefinedCallback || configurationCallback}
          title={title}
        >
          <i className={icon} aria-hidden="true"></i>
          {content && content}
        </Button>
      );
  };

  //accepts all the buttons and generates a dropdown with those buttons
  const createDropDownButton = (button) => {
    if (button.buttons.length === 1) {
      return generateOperationButton({ displayName: true, ...button.buttons[0] });
    } else {
      return (
        <SplitButton
          key={button.title}
          text={button.title}
          textField="name"
          onItemClick={button.predefinedCallback}
          itemRender={button.itemRender}
          className={button.className}
          iconClass={button.icon || ""}
          popupSettings={{ popupClass: "dropDownButton" }}
          primary={true}
          items={button.buttons}
        />
      );
    }
  };

  const generateOperationButtons = () => {
    // operationButtons should be an array of objects or a single object with the following structure:
    // {
    //   title: string - The text to display on the button or as a tooltip
    //   icon: string - CSS class for the icon (e.g., "fa-solid fa-arrows-rotate")
    //   onClick: function - Callback function to be executed when the button is clicked
    //   condition: boolean - Should evaluate to true or false; determines if the button should be rendered
    //   className: string (optional) - Additional CSS class(es) for styling the button
    //   content: string (optional) - Additional text content to display on the button
    // }
    let operationButtons =
      incomingOperations?.length > 0
        ? incomingOperations.flatMap((incomingOperation) => incomingOperation.operationButtons || [])
        : incomingOperations.operationButtons;
    let newOperationButtons = [];

    if (operationButtons) {
      if (Array.isArray(operationButtons)) {
        operationButtons.forEach((eachButton) => {
          if (eachButton.type === "DropDown") {
            const inlineButtons = eachButton.buttons.filter((each) => each.inline).map((each) => generateOperationButton(each));
            const dropDownButtons = eachButton.buttons.filter((each) => !each.inline);
            newOperationButtons = [...newOperationButtons, ...inlineButtons, createDropDownButton({ ...eachButton, buttons: dropDownButtons })];
          } else {
            let tempButton = generateOperationButton(eachButton);
            newOperationButtons = [...newOperationButtons, tempButton];
          }
        });
      } else {
        if (operationButtons?.type === "DropDown") {
          newOperationButtons = [createDropDownButton(operationButtons)];
        } else {
          operationButtons.forEach((eachButton) => {
            let tempButton = generateOperationButton(eachButton);
            newOperationButtons = [...newOperationButtons, tempButton];
          });
        }
      }
    }
    let uniqueOperationButtons = Array.from(new Map([...newOperationButtons].map((item) => [item.key, item])).values());
    return uniqueOperationButtons;
  };

  const clearOperationButtons = () => {
    setIncomingOperations([]);
    const page = navigationRef.current.getActivePage();
    if (page) {
      page.operationButtons = [];
      navigationRef.current.setPages(navigationRef.current.state.pages);
    }
  };

  //* LIFECYCLE METHODS
  useEffect(() => {
    let customEventHandlers = {
      setOperationButtons: (e) => {
        setTimeout(() => {
          updateOperationButtons(e.detail);
        }, 0);
      },
      resetOperationButtons,
      handleStepDownOfOperationButtons,
      clearOperationButtons,
    };
    for (const key in customEventHandlers) {
      document.getElementById(appNavigationDiv).addEventListener(key, customEventHandlers[key], false);
    }
  }, []);
  //* LIFECYCLE METHODS

  return (
    <div id="operationButtons_tempId" className="operationButtons dash-manager-buttons">
      {generateOperationButtons()}
    </div>
  );
};

export default OperationButtons;
